import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  SimpleGrid,
  useToast,
  Select,
  HStack,
  Tag,
  TagLabel,
  TagCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { createConsultant, updateConsultant } from "../../../api/consultantsApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { CONTRACT_TYPES } from "../../../constants/needs";
import {
  CONSULTANT_STATUSES,
  CONSULTANT_STATUS_LABELS,
  CONSULTANT_STATUS_LABEL_KEYS,
  CONSULTANT_STAFFING_STATUSES,
  CONSULTANT_STAFFING_STATUS_LABELS,
  CONSULTANT_STAFFING_STATUS_LABEL_KEYS,
  getStatusLabel
} from "../../../constants/statuses";

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value))
    return value;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

export const EMPTY_FORM = {
  name: "",
  title: "",
  email: "",
  phone: "",
  location: "",
  availability: "",
  status: "active",
  staffingStatus: "active",
  contractType: "",
  employmentStartDate: "",
  description: "",
  comments: "",
  skillsTechnical: [],
  skillsFunctional: [],
  skillsBusiness: [],
  minSalary: "",
  maxSalary: "",
  cvUrl: ""
};

const parseSkillsArray = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const ConsultantForm = ({
  initialData,
  onSuccess,
  onCancel,
  isEdit: isEditProp,
  showBackButton = false,
  onBack
}) => {
  const { selectedBusinessUnitId } = useBusinessUnit();
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateConsultantId, setDuplicateConsultantId] = useState(null);
  const duplicateDialog = useDisclosure();
  const duplicateCancelRef = useRef();
  const [skillInput, setSkillInput] = useState({
    technical: "",
    functional: "",
    business: ""
  });
  const toast = useToast();
  const { t } = useTranslation("portal");

  const isEdit = isEditProp ?? Boolean(initialData?.id);

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || "",
        title: initialData.title || "",
        email: initialData.email || "",
        phone: initialData.phone || "",
        location: initialData.location || "",
        availability: initialData.availability || "",
        status: initialData.status || "",
        staffingStatus:
          initialData.staffingStatus ||
          initialData.staffing_status ||
          "active",
        contractType:
          initialData.contractType || initialData.contract_type || "",
        employmentStartDate: toDateInputValue(
          initialData.employmentStartDate || initialData.employment_start_date
        ),
        description: initialData.description || "",
        comments: initialData.comments || "",
        skillsTechnical: parseSkillsArray(
          initialData.skillsTechnical || initialData.skills_technical
        ),
        skillsFunctional: parseSkillsArray(
          initialData.skillsFunctional || initialData.skills_functional
        ),
        skillsBusiness: parseSkillsArray(
          initialData.skillsBusiness || initialData.skills_business
        ),
        minSalary:
          initialData.minSalary != null
            ? String(initialData.minSalary)
            : initialData.min_salary != null
            ? String(initialData.min_salary)
            : "",
        maxSalary:
          initialData.maxSalary != null
            ? String(initialData.maxSalary)
            : initialData.max_salary != null
            ? String(initialData.max_salary)
            : "",
        cvUrl: initialData.cv_url || initialData.cvUrl || ""
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setSkillInput({ technical: "", functional: "", business: "" });
  }, [initialData, isEdit]);

  const isValid = useMemo(() => {
    return (
      form.name.trim().length > 0 &&
      form.status.trim().length > 0 &&
      form.staffingStatus.trim().length > 0
    );
  }, [form.name, form.status, form.staffingStatus]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSkillAdd = (type) => {
    const inputValue = skillInput[type].trim();
    if (!inputValue) return;
    const fieldName =
      type === "technical"
        ? "skillsTechnical"
        : type === "functional"
        ? "skillsFunctional"
        : "skillsBusiness";
    setForm((prev) => ({
      ...prev,
      [fieldName]: [...prev[fieldName], inputValue]
    }));
    setSkillInput((prev) => ({ ...prev, [type]: "" }));
  };

  const handleSkillRemove = (type, index) => {
    const fieldName =
      type === "technical"
        ? "skillsTechnical"
        : type === "functional"
        ? "skillsFunctional"
        : "skillsBusiness";
    setForm((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      const payload = {
        name: form.name,
        title: form.title || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        location: form.location || undefined,
        availability: form.availability || undefined,
        status: form.status || undefined,
        staffingStatus: form.staffingStatus || undefined,
        description: form.description || undefined,
        comments: form.comments || undefined,
        cv_url: form.cvUrl || undefined,
        skillsTechnical:
          form.skillsTechnical && form.skillsTechnical.length > 0
            ? form.skillsTechnical
            : undefined,
        skillsFunctional:
          form.skillsFunctional && form.skillsFunctional.length > 0
            ? form.skillsFunctional
            : undefined,
        skillsBusiness:
          form.skillsBusiness && form.skillsBusiness.length > 0
            ? form.skillsBusiness
            : undefined,
        minSalary:
          form.minSalary && !Number.isNaN(Number(form.minSalary))
            ? Number(form.minSalary)
            : undefined,
        maxSalary:
          form.maxSalary && !Number.isNaN(Number(form.maxSalary))
            ? Number(form.maxSalary)
            : undefined,
        contractType: form.contractType || undefined,
        employmentStartDate: form.employmentStartDate || undefined
      };
      if (isEdit) {
        const updated = await updateConsultant(initialData.id, payload, {
          buId: selectedBusinessUnitId || undefined
        });
        toast({
          title: t("pilotConsultants.form.toasts.updated"),
          status: "success"
        });
        onSuccess?.(updated);
      } else {
        const created = await createConsultant(payload, {
          buId: selectedBusinessUnitId || undefined
        });
        toast({
          title: t("pilotConsultants.form.toasts.created"),
          status: "success"
        });
        onSuccess?.(created);
      }
      onCancel?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (
        e?.response?.status === 409 &&
        (detail?.code === "DUPLICATE_CONSULTANT" || detail === "DUPLICATE_CONSULTANT")
      ) {
        const existingId = detail?.existingId ?? e?.response?.data?.existingId ?? null;
        setDuplicateConsultantId(existingId);
        duplicateDialog.onOpen();
      } else {
        toast({
          title: t("pilotConsultants.form.toasts.operationFailedTitle"),
          description:
            e?.response?.data?.message ||
            t("pilotConsultants.form.toasts.operationFailedDescription"),
          status: "error"
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel>{t("pilotConsultants.form.fields.name")}</FormLabel>
          <Input
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder={t("pilotConsultants.form.placeholders.name")}
          />
        </FormControl>
        <FormControl>
          <FormLabel>{t("pilotConsultants.form.fields.title")}</FormLabel>
          <Input
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder={t("pilotConsultants.form.placeholders.title")}
          />
        </FormControl>
        <FormControl>
          <FormLabel>{t("pilotConsultants.form.fields.email")}</FormLabel>
          <Input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder={t("pilotConsultants.form.placeholders.email")}
          />
        </FormControl>
        <FormControl>
          <FormLabel>{t("pilotConsultants.form.fields.phone")}</FormLabel>
          <Input
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder={t("pilotConsultants.form.placeholders.phone")}
          />
        </FormControl>
        <FormControl>
          <FormLabel>{t("pilotConsultants.form.fields.location")}</FormLabel>
          <Input
            name="location"
            value={form.location}
            onChange={handleChange}
            placeholder={t("pilotConsultants.form.placeholders.location")}
          />
        </FormControl>
        <FormControl>
          <FormLabel>
            {t("pilotConsultants.form.fields.availability")}
          </FormLabel>
          <Input
            name="availability"
            value={form.availability}
            onChange={handleChange}
            placeholder={t("pilotConsultants.form.placeholders.availability")}
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>{t("pilotConsultants.form.fields.status")}</FormLabel>
          <Select
            name="status"
            value={form.status}
            onChange={handleChange}
            placeholder={t("pilotConsultants.form.placeholders.status")}
            sx={{
              paddingTop: "0 !important",
              paddingBottom: "0 !important",
              lineHeight: "32px !important",
              height: "32px !important"
            }}
            paddingTop="0"
            paddingBottom="0"
          >
            {CONSULTANT_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {getStatusLabel(
                  t,
                  CONSULTANT_STATUS_LABEL_KEYS,
                  CONSULTANT_STATUS_LABELS,
                  s.value
                )}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl isRequired>
          <FormLabel>
            {t("pilotConsultants.form.fields.staffingStatus")}
          </FormLabel>
          <Select
            name="staffingStatus"
            value={form.staffingStatus}
            onChange={handleChange}
            placeholder={t(
              "pilotConsultants.form.placeholders.staffingStatus"
            )}
            sx={{
              paddingTop: "0 !important",
              paddingBottom: "0 !important",
              lineHeight: "32px !important",
              height: "32px !important"
            }}
            paddingTop="0"
            paddingBottom="0"
          >
            {CONSULTANT_STAFFING_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {getStatusLabel(
                  t,
                  CONSULTANT_STAFFING_STATUS_LABEL_KEYS,
                  CONSULTANT_STAFFING_STATUS_LABELS,
                  s.value
                )}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>
            {t("pilotConsultants.form.fields.contractType", {
              defaultValue: "Contract type"
            })}
          </FormLabel>
          <Select
            name="contractType"
            value={form.contractType}
            onChange={handleChange}
            placeholder={t(
              "pilotConsultants.form.placeholders.contractType",
              { defaultValue: "Select contract type" }
            )}
            sx={{
              paddingTop: "0 !important",
              paddingBottom: "0 !important",
              lineHeight: "32px !important",
              height: "32px !important"
            }}
            paddingTop="0"
            paddingBottom="0"
          >
            {CONTRACT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>
            {t("pilotConsultants.form.fields.employmentStartDate", {
              defaultValue: "Employment start date"
            })}
          </FormLabel>
          <Input
            type="date"
            name="employmentStartDate"
            value={form.employmentStartDate}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl>
          <FormLabel>{t("pilotConsultants.form.fields.minSalary")}</FormLabel>
          <Input
            type="number"
            name="minSalary"
            value={form.minSalary}
            onChange={handleChange}
            placeholder={t("pilotConsultants.form.placeholders.minSalary")}
          />
        </FormControl>
        <FormControl>
          <FormLabel>{t("pilotConsultants.form.fields.maxSalary")}</FormLabel>
          <Input
            type="number"
            name="maxSalary"
            value={form.maxSalary}
            onChange={handleChange}
            placeholder={t("pilotConsultants.form.placeholders.maxSalary")}
          />
        </FormControl>
      </SimpleGrid>
      <FormControl mt={4}>
        <FormLabel>{t("pilotConsultants.form.fields.description")}</FormLabel>
        <Textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          placeholder={t("pilotConsultants.form.placeholders.description")}
        />
      </FormControl>
      <FormControl mt={4}>
        <FormLabel>{t("pilotConsultants.form.fields.comments")}</FormLabel>
        <Textarea
          name="comments"
          value={form.comments}
          onChange={handleChange}
          placeholder={t("pilotConsultants.form.placeholders.comments")}
        />
      </FormControl>

      <FormControl mt={4}>
        <FormLabel>
          {t("pilotConsultants.form.fields.technicalSkills")}
        </FormLabel>
        <HStack mb={2}>
          <Input
            placeholder={t(
              "pilotConsultants.form.placeholders.technicalSkill"
            )}
            value={skillInput.technical}
            onChange={(e) =>
              setSkillInput((prev) => ({ ...prev, technical: e.target.value }))
            }
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSkillAdd("technical");
              }
            }}
          />
          <Button onClick={() => handleSkillAdd("technical")} size="sm">
            {t("pilotConsultants.form.buttons.addSkill")}
          </Button>
        </HStack>
        <HStack wrap="wrap" spacing={2}>
          {form.skillsTechnical.map((skill, idx) => (
            <Tag key={idx} size="md" variant="solid" colorScheme="purple">
              <TagLabel>{skill}</TagLabel>
              <TagCloseButton
                onClick={() => handleSkillRemove("technical", idx)}
              />
            </Tag>
          ))}
        </HStack>
      </FormControl>

      <FormControl mt={4}>
        <FormLabel>
          {t("pilotConsultants.form.fields.functionalSkills")}
        </FormLabel>
        <HStack mb={2}>
          <Input
            placeholder={t(
              "pilotConsultants.form.placeholders.functionalSkill"
            )}
            value={skillInput.functional}
            onChange={(e) =>
              setSkillInput((prev) => ({
                ...prev,
                functional: e.target.value
              }))
            }
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSkillAdd("functional");
              }
            }}
          />
          <Button onClick={() => handleSkillAdd("functional")} size="sm">
            {t("pilotConsultants.form.buttons.addSkill")}
          </Button>
        </HStack>
        <HStack wrap="wrap" spacing={2}>
          {form.skillsFunctional.map((skill, idx) => (
            <Tag key={idx} size="md" variant="solid" colorScheme="orange">
              <TagLabel>{skill}</TagLabel>
              <TagCloseButton
                onClick={() => handleSkillRemove("functional", idx)}
              />
            </Tag>
          ))}
        </HStack>
      </FormControl>

      <FormControl mt={4}>
        <FormLabel>
          {t("pilotConsultants.form.fields.businessSkills")}
        </FormLabel>
        <HStack mb={2}>
          <Input
            placeholder={t(
              "pilotConsultants.form.placeholders.businessSkill"
            )}
            value={skillInput.business}
            onChange={(e) =>
              setSkillInput((prev) => ({ ...prev, business: e.target.value }))
            }
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSkillAdd("business");
              }
            }}
          />
          <Button onClick={() => handleSkillAdd("business")} size="sm">
            {t("pilotConsultants.form.buttons.addSkill")}
          </Button>
        </HStack>
        <HStack wrap="wrap" spacing={2}>
          {form.skillsBusiness.map((skill, idx) => (
            <Tag key={idx} size="md" variant="solid" colorScheme="green">
              <TagLabel>{skill}</TagLabel>
              <TagCloseButton
                onClick={() => handleSkillRemove("business", idx)}
              />
            </Tag>
          ))}
        </HStack>
      </FormControl>

      <HStack justify="flex-end" mt={6} spacing={3}>
        {showBackButton && onBack && (
          <Button variant="ghost" onClick={onBack}>
            {t("pilotConsultants.createWizard.buttons.back")}
          </Button>
        )}
        <Button variant="ghost" onClick={onCancel}>
          {t("pilotConsultants.form.buttons.cancel")}
        </Button>
        <Button
          colorScheme="teal"
          onClick={handleSubmit}
          isDisabled={!isValid}
          isLoading={isSubmitting}
        >
          {isEdit
            ? t("pilotConsultants.form.buttons.saveChanges")
            : t("pilotConsultants.form.buttons.create")}
        </Button>
      </HStack>

      <AlertDialog
        isOpen={duplicateDialog.isOpen}
        leastDestructiveRef={duplicateCancelRef}
        onClose={() => { setDuplicateConsultantId(null); duplicateDialog.onClose(); }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("pilotConsultants.form.duplicate.title", { defaultValue: "Duplicate Consultant" })}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t("pilotConsultants.form.duplicate.body", {
                defaultValue: "A consultant with this name already exists. Would you like to view the existing profile?"
              })}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={duplicateCancelRef} onClick={() => { setDuplicateConsultantId(null); duplicateDialog.onClose(); }}>
                {t("common.cancel", { defaultValue: "Cancel" })}
              </Button>
              {duplicateConsultantId && (
                <Button
                  colorScheme="teal"
                  ml={3}
                  onClick={() => {
                    duplicateDialog.onClose();
                    onSuccess?.({ id: duplicateConsultantId, _viewExisting: true });
                    onCancel?.();
                  }}
                >
                  {t("pilotConsultants.form.duplicate.viewExisting", { defaultValue: "View Existing" })}
                </Button>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ConsultantForm;
